/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ProgressBar } from '@tag/tag-components-react-v4';
import { MenuItem, Button as TagButton, ButtonType as TagButtonType } from '@access-erp/ui-components';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import { TableGrid } from '@/components/common/TableGrid/TableGrid';
import { 
  parseFile, 
  generateCSV, 
  generateExcel, 
  downloadFile, 
  ContractUpdateRow, 
  ProcessingStatus 
} from './utils/fileParser';
import NoRowsOverlay from './components/NoRowsOverlay';
import StatusCellRenderer from './components/StatusCellRenderer';
import { notificationService } from '@/services/notification/notification.client';
import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { UpdateContract, UpdateContractVariables } from '@/graphql/contracts/_generated_/updateContract.mutation';
import UpdateContractMutation from '@/graphql/contracts/updateContract.mutation.gql';
import { UserRole } from '@/config/roles.config';
import styles from './bulk-update-contract-modal.module.scss';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import useGetContractIdentifiers from '@/hooks/contract/useGetContractIdentifiers.hook';
import { NumberUtils } from '@/utils/numbers.utils';

dayjs.extend(customParseFormat);

interface BulkUpdateContractModalProps {
  onClose: () => void;
  userRole?: UserRole;
}


export default function BulkUpdateContractModal({ onClose, userRole }: BulkUpdateContractModalProps) {
  const [tableData, setTableData] = useState<ContractUpdateRow[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [originalFileFormat, setOriginalFileFormat] = useState<'csv' | 'excel' | null>(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<any>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const currentIndexRef = useRef(0);
  const abortRef = useRef(false);
  const isNotFullAccess = userRole !== UserRole.FULL_ACCESS;

  const { getContractIdentifiers } = useGetContractIdentifiers();

  const [updateContract] = useMutation<UpdateContract, UpdateContractVariables>(
    UpdateContractMutation,
    {
      client: apolloClientContracts,
      context: { skipLoadingAnimation: true },
    },
  );

  const columnDefs = [
    {
      headerName: 'Status',
      field: 'status',
      cellRenderer: StatusCellRenderer,
      sortable: false,
      filter: false,
      width: 80,
      pinned: 'left' as const
    },
    { headerName: 'Contract ID', field: 'contractId', sortable: false, filter: false, width: 150 },
    { headerName: 'Contract Number', field: 'contractNumber', sortable: false, filter: false, width: 150 },
    { headerName: 'Contract Start Date (YYYY-MM-DD)', 
      field: 'contractStartDate', sortable: false, filter: false, width: 250 },
    { headerName: 'Contract End Date (YYYY-MM-DD)', 
      field: 'contractEndDate', sortable: false, filter: false, width: 250 },
    { headerName: 'Initial Term', field: 'initialTerm', sortable: false, filter: false, width: 120 },
    { headerName: 'Renewable Term', field: 'renewableTerm', sortable: false, filter: false, width: 140 },
    { headerName: 'Errors', field: 'errors', sortable: false, filter: false, width: 500 },
  ];

  const handleFileUpload = async (file: File) => {
    try {
      const parsedData = await parseFile(file);

      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const format = fileExtension === 'csv' ? 'csv' : 'excel';

      const dataWithStatus: ContractUpdateRow[] = parsedData.map(row => 
        ({ ...row, status: row.status || 'pending' as ProcessingStatus }));

      const rowsNeedingResolution: Array<{ index: number; contractId?: string; contractNumber?: string }> = [];
      
      dataWithStatus.forEach((row, index) => {
        if (row.status === 'error') {
          return;
        }
        
        const hasContractId = row.contractId && row.contractId.trim() !== '';
        const hasContractNumber = row.contractNumber && row.contractNumber.trim() !== '';
        
        if (!hasContractId && !hasContractNumber) {
          dataWithStatus[index].errors = 'Missing both Contract ID and Contract Number';
          dataWithStatus[index].status = 'error';
          return;
        }
        
        if (hasContractId && hasContractNumber) {
          return;
        }
        
        if (hasContractId || hasContractNumber) {
          rowsNeedingResolution.push({
            index,
            contractId: hasContractId ? row.contractId : undefined,
            contractNumber: hasContractNumber ? row.contractNumber : undefined,
          });
        }
      });

      if (rowsNeedingResolution.length > 0) {
        try {
          const input = rowsNeedingResolution.map(row => ({
            contractId: row.contractId || null,
            contractNumber: row.contractNumber || null,
          }));

          const identifiers = await getContractIdentifiers(input);

          if (identifiers) {
            identifiers.forEach((identifier, _) => {
              const rowIndex = dataWithStatus.findIndex((x) => 
                x.contractId?.trim().toLowerCase() === identifier.contractId.trim().toLowerCase() || 
                x.contractNumber?.trim().toLowerCase() === identifier.contractNumber.trim().toLowerCase());
              
              if (rowIndex === -1) return;

              if (identifier.contractId) {
                dataWithStatus[rowIndex].contractId = identifier.contractId;
              }
              if (identifier.contractNumber) {
                dataWithStatus[rowIndex].contractNumber = identifier.contractNumber;
              }
            });
          }
        } catch (error: any) {
          notificationService.open({
            title: 'Warning',
            text: `Failed to resolve some contract identifiers: ${error?.message || 'Unknown error'}`,
            type: 'warning'
          });
        }
      }

      setTableData(dataWithStatus);
      setOriginalFileFormat(format);
      setProcessingComplete(false);
      setProcessedCount(0);
      currentIndexRef.current = 0;
      try {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.warn('Error clearing file input after upload:', error);
      }
      notificationService.open({
        title: 'File Upload',
        text: `Successfully loaded ${parsedData.length} contracts`,
        type: 'success'
      });
    } catch (error: any) {
      notificationService.open({
        title: 'Error',
        text: error?.message || 'Failed to parse file',
        type: 'danger'
      });
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleUploadButtonClick = () => {
    try {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    } catch (error) {
      console.warn('Error triggering file input click:', error);
    }
  };

  const handleClearAll = () => {
    setTableData([]);
    setOriginalFileFormat(null);
    setProcessingComplete(false);
    setProcessedCount(0);
    currentIndexRef.current = 0;
    try {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.warn('Error clearing file input:', error);
    }
  };

  const DATE_FORMAT = 'YYYY-MM-DD';

  const formatDateToISO = (dateString: string): string | undefined => {
    if (!dateString) return undefined;
    const trimmed = dateString.trim();
    if (!trimmed) return undefined;
    const d = dayjs(trimmed, DATE_FORMAT, true);
    if (d.isValid()) {
      return `${d.format('YYYY-MM-DD')}T00:00:00`;
    }
    return undefined;
  };

  const processContracts = async (): Promise<boolean> => {
    const updatedData = [...tableData];
    
    for (let i = currentIndexRef.current; i < updatedData.length; i++) {
      if (abortRef.current) {
        return false;
      }

      const row = updatedData[i];

      if (row.status === 'error' && row.errors) {
        currentIndexRef.current = i + 1;
        setProcessedCount(i + 1);
        continue;
      }

      updatedData[i].status = 'processing';
      setTableData([...updatedData]);

      try {
        const nullValues = [null, undefined, ''];
        const renewableTermValue = typeof row.renewableTerm !== 'number' && nullValues.includes(row.renewableTerm)
          ? undefined : NumberUtils.parseIntegerInput(row.renewableTerm?.toString()!);

        const initialTermValue = typeof row.initialTerm !== 'number' && nullValues.includes(row.initialTerm)
          ? undefined : NumberUtils.parseIntegerInput(row.initialTerm?.toString()!);

        const contractStartDateISO = formatDateToISO(row.contractStartDate);
        const contractEndDateISO = formatDateToISO(row.contractEndDate);

        await updateContract({
          variables: {
            input: {
              contractId: row.contractId,
              contractStartDate: contractStartDateISO,
              contractEndDate: contractEndDateISO,
              renewableTerm: renewableTermValue,
              initialTerm: initialTermValue,
              externalContractID: undefined,
            },
          },
        });

        updatedData[i].errors = '';
        updatedData[i].status = 'success';
        currentIndexRef.current = i + 1;
      } catch (error: any) {
        const errorMessage = error?.message || error?.graphQLErrors?.[0]?.message || 'Unknown error occurred';
        updatedData[i].errors = errorMessage.trim();
        updatedData[i].status = 'error';
        currentIndexRef.current = i + 1;
      }

      setTableData([...updatedData]);
      setProcessedCount(i + 1);
    }

    return true;
  };

  const handleSubmit = async () => {
    if (tableData.length === 0) return;
    setShowDisclaimer(true);
  };

  const handleConfirmSubmit = async () => {
    setShowDisclaimer(false);
    setIsProcessing(true);
    
    const isResuming = currentIndexRef.current > 0 && currentIndexRef.current < tableData.length;
    const isCompleted = currentIndexRef.current >= tableData.length;
    
    if (isCompleted || currentIndexRef.current === 0) {
      setProcessedCount(0);
      currentIndexRef.current = 0;
    } else if (isResuming) {
      setProcessedCount(currentIndexRef.current);
    }
    
    setTotalCount(tableData.length);
    abortRef.current = false;

    const completed = await processContracts();
    
    if (completed) {
      setIsProcessing(false);
      setProcessingComplete(true);

      const errorCount = tableData.filter(row => row.errors).length;
      const successCount = tableData.filter(row => row.status === 'success').length;

      notificationService.open({
        title: 'Bulk Update Complete',
        text: `${successCount} contracts updated successfully, ${errorCount} failed`,
        type: errorCount > 0 ? 'warning' : 'success'
      });
    }
  };

  const handleAbort = () => {
    abortRef.current = true;
    setIsProcessing(false);
  };

  const handleDownloadResults = (filterType: 'all' | 'success' | 'error' = 'all') => {
    let filteredData = tableData;
    
    if (filterType === 'success') {
      filteredData = tableData.filter(row => row.status === 'success');
    } else if (filterType === 'error') {
      filteredData = tableData.filter(row => row.status === 'error');
    }

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const suffix = filterType === 'all' ? '' : `-${filterType}`;

    if (originalFileFormat === 'excel') {
      const excelBuffer = generateExcel(filteredData);
      const filename = `bulk-update-results${suffix}-${timestamp}.xlsx`;
      downloadFile(excelBuffer, filename, 'excel');
    } else {
      const csv = generateCSV(filteredData);
      const filename = `bulk-update-results${suffix}-${timestamp}.csv`;
      downloadFile(csv, filename, 'csv');
    }
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/public/assets/download-templates/BulkUpdateContractsTemplate.csv';
    link.download = 'BulkUpdateContractsTemplate.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div ref={modalRef}>
        <AgModalWindow
          title="Bulk Update Contracts"
          isOpen={true}
          onClose={isProcessing ? () => {} : onClose}
          contentStyle={{ 
            width: isMaximized ? '80vw' : '1000px', 
            maxHeight: isMaximized ? '1500px' : '1200px'
          }}
          maximizeState={{ isMaximized, setIsMaximized }}
        >
        <div className={styles['modal-content']}>
          
          <div className={styles['button-row']}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileInputChange}
              className={styles['hidden-file-input']}
            />
            <Button onClick={handleUploadButtonClick} disabled={isProcessing || isNotFullAccess}>
              Upload Excel/CSV
            </Button>
            <Button
              onClick={handleClearAll}
              disabled={tableData.length === 0 || isProcessing || isNotFullAccess}
            >
              Clear All
            </Button>
            <Button onClick={handleDownloadTemplate} disabled={isProcessing || isNotFullAccess}>
              Download Template
            </Button>
          </div>

          <div className={isMaximized ? styles['table-container-maximized'] : styles['table-container']}>
            <TableGrid
              ref={gridRef}
              columnDefs={columnDefs}
              rowData={tableData}
              pagination={false}
              paginationPageSize={undefined}
              suppressPaginationPanel={true}
              domLayout="normal"
              suppressHorizontalScroll={false}
              noRowsOverlayComponent={NoRowsOverlay}
              defaultColDef={{ resizable: true }}
              onGridReady={(params) => {
                gridRef.current = params.api;
                params.api.sizeColumnsToFit();
              }}
            />
          </div>

          {tableData.length > 0 && (
            <div className={styles['table-info']}>
              Showing {tableData.length} contract{tableData.length !== 1 ? 's' : ''}
            </div>
          )}

          {isProcessing && (
            <div className={styles['progress-section']}>
              <div className={styles['progress-text']}>
                Processed {processedCount} out of {totalCount}
              </div>
              <ProgressBar value={Math.round((processedCount / totalCount) * 100)} type="success" />
            </div>
          )}

          <div className={styles['action-buttons']}>
            {!isProcessing && !processingComplete && (
              <>
                <Button 
                  onClick={handleSubmit} 
                  disabled={tableData.length === 0 || isNotFullAccess}
                  buttonStyleType={ButtonStyleType.PRIMARY}
                >
                  Submit
                </Button>
                <Button 
                  onClick={onClose} 
                  disabled={isNotFullAccess}
                  buttonStyleType={ButtonStyleType.SECONDARY}
                  className="ml-10"
                >
                  Cancel
                </Button>
              </>
            )}

            {isProcessing && (
              <Button 
                onClick={handleAbort} 
                disabled={isNotFullAccess}
                buttonStyleType={ButtonStyleType.WARNING}
              >
                Abort
              </Button>
            )}

            {processingComplete && (
              <>
                <TagButton 
                  disabled={isNotFullAccess}
                  buttonType={TagButtonType.Primary}
                  text="Export Results"
                  wrapperStyle={{ flex: 0 }}
                  onMenuItemClick={({ name }) => {
                    if (name === 'All') {
                      handleDownloadResults('all');
                    } else if (name === 'Success') {
                      handleDownloadResults('success');
                    } else if (name === 'Error') {
                      handleDownloadResults('error');
                    }
                  }}
                >
                  <MenuItem name="All" text="All" />
                  <MenuItem name="Success" text="Success" />
                  <MenuItem name="Error" text="Error" />
                </TagButton>
                <Button 
                  onClick={onClose} 
                  disabled={isNotFullAccess}
                  buttonStyleType={ButtonStyleType.SECONDARY}
                  className="ml-10"
                >
                  Close
                </Button>
              </>
            )}
          </div>
        </div>
        </AgModalWindow>
      </div>

      {showDisclaimer && (
        <AgConfirmationModal
          title="Important Notice"
          isOpen={true}
          onClose={() => setShowDisclaimer(false)}
          contentStyle={{ minWidth: '400px', maxWidth: '500px' }}
          onConfirm={handleConfirmSubmit}
        >
          <div style={{ padding: '20px' }}>
            Please do not close the tab, close the browser, or refresh the page while processing the data.
          </div>
        </AgConfirmationModal>
      )}
    </>
  );
}
